/**
 * App-wide classification of an unknown infrastructure/transport error into a
 * coarse `network` vs `api` failure.
 *
 * Domain-agnostic: feature error models (e.g. `ListWizardError`,
 * `LicencesError`) build their own discriminated unions on top of this instead
 * of each re-implementing the same detection. Duck-typed on purpose so it never
 * depends on the api client; any error carrying a numeric `status` is an API
 * error; a `TypeError` (or network-shaped message) is a network error; anything
 * else is a generic API error with status 0. Pure and framework-free.
 */

/** A transport/connection failure (offline, DNS, CORS). */
export interface NetworkFailure {
  kind: 'network';
  message: string;
}

/** A failure the backend answered (carries an HTTP-ish status; 0 = unknown). */
export interface ApiFailure {
  kind: 'api';
  message: string;
  status: number;
}

export type InfraFailure = NetworkFailure | ApiFailure;

/** User-facing copy for a network failure (shared across features). */
export const NETWORK_ERROR_MESSAGE = 'Network error; please check your connection and retry.';

/** Does this look like a browser fetch/network failure (offline, DNS, CORS)? */
export function isNetworkError(e: unknown): boolean {
  if (e instanceof TypeError) return true;
  if (e instanceof Error) {
    return /failed to fetch|network|load failed/i.test(e.message);
  }
  return false;
}

/**
 * Map an unknown infra error into a `network` | `api` failure. `defaultMessage`
 * is used only when the error carries no usable message of its own.
 */
export function classifyInfraError(e: unknown, defaultMessage: string): InfraFailure {
  if (isNetworkError(e)) {
    return { kind: 'network', message: NETWORK_ERROR_MESSAGE };
  }
  const status = (e as { status?: unknown })?.status;
  const message = e instanceof Error && e.message ? e.message : defaultMessage;
  return { kind: 'api', message, status: typeof status === 'number' ? status : 0 };
}
